import is from '@sindresorhus/is';
import { type FunctionComponent, useMemo } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { Badge } from '~/ui/core/badge/Badge';

import type { TaskManagementTaskDetail, TaskManagementTaskRef } from '../../data-contract/task-management';
import { createSkillDetailTilePayload } from '../../skills';
import { useUrlTiles } from '../../url-tiles';
import { useTaskManagementClusters } from '../api/use-task-management-clusters';
import { useJobArchitecturePermissions } from '../context/JobArchitecturePermissionsContext';
import { mapProfileTaskAutomation, workflowStepIdAt } from '../profile-tasks/api/profile-task-automation-adapter';
import { createAutomationAssessmentTilePayload } from '../profile-tasks/automation-assessment/automation-assessment-tile-url';
import { createAutomationRecommendationTilePayload } from '../profile-tasks/automation-assessment/automation-recommendation-tile-url';
import { createAutomationStepTilePayload } from '../profile-tasks/automation-assessment/automation-step-tile-url';
import { createBusinessImpactTilePayload } from '../profile-tasks/automation-assessment/business-impact-tile-url';
import {
    getProfileTaskClassificationTranslationKey,
    profileTaskTranslationKeys,
} from '../profile-tasks/profile-task-translations';
import {
    type TaskDetail,
    type TaskDetailAutomationActions,
    type TaskDetailCommentable,
    TaskDetailFields,
} from '../profile-tasks/tile/TaskDetailFields';
import type { TaskMetaRow } from '../profile-tasks/tile/TaskFieldPrimitives';
import { useTaskManagementArchitecture } from '../tasks/use-task-management-architecture';
import { useWorkTaskTileHidesHierarchy } from './work-task-tile-sections';

/** The task's own meta rows (classification, effort), without the architecture's clusters. */
export const useTaskDetailMetaRows = (task: TaskManagementTaskDetail): readonly TaskMetaRow[] => {
    const translate = useTranslate();
    const rows: TaskMetaRow[] = [];
    const classification = task.classification?.value;
    const { maximumHoursPerWeek, minimumHoursPerWeek } = task.estimatedEffort ?? {};

    if (!is.undefined(classification)) {
        rows.push({
            key: 'classification',
            label: translate(profileTaskTranslationKeys.classification),
            tooltip: task.classification?.reasoning,
            value: (
                <Badge
                    data-testid="work-task-classification"
                    variant={classification === 'contribution' ? 'success' : 'gray'}
                >
                    {translate(getProfileTaskClassificationTranslationKey(classification))}
                </Badge>
            ),
        });
    }

    if (!is.undefined(minimumHoursPerWeek) || !is.undefined(maximumHoursPerWeek)) {
        rows.push({
            key: 'effort',
            label: translate(profileTaskTranslationKeys.effort),
            tooltip: task.estimatedEffort?.reasoning,
            value: (
                <Badge data-testid="work-task-effort" variant="gray">
                    {translate(profileTaskTranslationKeys.effortFormat, {
                        maximum: maximumHoursPerWeek ?? minimumHoursPerWeek,
                        minimum: minimumHoursPerWeek ?? maximumHoursPerWeek,
                    })}
                </Badge>
            ),
        });
    }

    return rows;
};

/** The details card's rows; the clusters row needs architecture read and is left out without it. */
const useMetaRows = (task: TaskManagementTaskDetail, canRead: boolean): readonly TaskMetaRow[] => {
    const translate = useTranslate();
    const { architectureId } = useTaskManagementArchitecture({ queriesEnabled: canRead });
    const { data: taskGroups } = useTaskManagementClusters({ architectureId });
    const detailRows = useTaskDetailMetaRows(task);
    const clusters =
        taskGroups?.flatMap((cluster) =>
            cluster.id !== null && cluster.name !== null && cluster.taskIds.includes(task.id)
                ? [{ id: cluster.id, name: cluster.name }]
                : [],
        ) ?? [];

    if (architectureId === undefined || clusters.length === 0) return detailRows;

    return [
        {
            key: 'clusters',
            label: translate(profileTaskTranslationKeys.clusters),
            value: (
                <ul className="m-0 flex list-none flex-wrap justify-end gap-xs p-0" data-testid="work-task-clusters">
                    {clusters.map((cluster) => (
                        <li key={cluster.id}>
                            <Badge variant="gray">{cluster.name}</Badge>
                        </li>
                    ))}
                </ul>
            ),
        },
        ...detailRows,
    ];
};

type WorkTaskTileFieldsProps = {
    readonly activeLanguage: LanguageCodeForJSA;
    readonly commentable?: TaskDetailCommentable;
    readonly onOpenTask: (task: TaskManagementTaskRef) => void;
    readonly task: TaskManagementTaskDetail;
};

export const WorkTaskTileFields: FunctionComponent<WorkTaskTileFieldsProps> = ({ onOpenTask, ...props }) => {
    const { canRead } = useJobArchitecturePermissions();
    const { openTile } = useUrlTiles();
    const { activeLanguage, task } = props;
    const address = { taskId: task.id, taskVersionId: String(task.version) };

    return (
        <WorkTaskTileFieldsView
            {...props}
            automationActions={{
                openAssessment: () => openTile(createAutomationAssessmentTilePayload(address)),
                openBusinessImpact: () => openTile(createBusinessImpactTilePayload(address)),
                openRecommendation: (automationId) =>
                    openTile(
                        createAutomationRecommendationTilePayload({
                            ...address,
                            automationId,
                            language: activeLanguage,
                        }),
                    ),
                openStep: (workflowStepId) => openTile(createAutomationStepTilePayload({ ...address, workflowStepId })),
            }}
            metaRows={useMetaRows(task, canRead)}
            onOpenTask={canRead ? onOpenTask : undefined}
        />
    );
};

type WorkTaskTileFieldsViewProps = Omit<WorkTaskTileFieldsProps, 'onOpenTask'> & {
    /** Opens the automation tiles; without it the scores and recommendations are shown only. */
    readonly automationActions?: TaskDetailAutomationActions;
    /** Leaves out the task's parents and children; a page can also leave them out for every tile it hosts. */
    readonly hidesHierarchy?: boolean;
    readonly metaRows: readonly TaskMetaRow[];
    /** Opens a related task; without it the related tasks are listed only. */
    readonly onOpenTask: ((task: TaskManagementTaskRef) => void) | undefined;
};

/** The work task's fields from given meta rows, for surfaces outside a job architecture. */
export const WorkTaskTileFieldsView: FunctionComponent<WorkTaskTileFieldsViewProps> = ({
    activeLanguage,
    automationActions,
    commentable,
    hidesHierarchy = false,
    metaRows,
    onOpenTask,
    task,
}) => {
    const { updateTile } = useUrlTiles();
    const pageHidesHierarchy = useWorkTaskTileHidesHierarchy();
    const detail = useMemo(
        (): TaskDetail => ({
            automation: mapProfileTaskAutomation(task),
            description: task.description,
            effort: task.estimatedEffort,
            skills: [...task.requiredSkills]
                .sort((left, right) => left.order - right.order)
                .map((skill) => ({ level: skill.level, skillId: skill.id, title: skill.title, type: skill.skillType })),
            taskId: task.id,
            workflowSteps: (task.workflow?.steps ?? []).map((step, index) => ({
                description: step.description,
                title: step.title,
                workflowStepId: workflowStepIdAt(index),
            })),
        }),
        [task],
    );
    return (
        <TaskDetailFields
            {...(automationActions === undefined ? {} : { automationActions })}
            {...(commentable === undefined ? {} : { commentable })}
            metaRows={metaRows}
            relations={{
                children: task.children,
                hidesHierarchy: hidesHierarchy || pageHidesHierarchy,
                onOpenTask,
                parents: task.parents,
                related: task.related,
            }}
            task={detail}
            testIdPrefix="work-task"
            // Built by hand: the work task tile renders outside the architecture scope that
            // useJobArchitectureUrlTileActions asserts on.
            onOpenSkill={(skill) =>
                updateTile(
                    createSkillDetailTilePayload({
                        preferredLanguage: activeLanguage,
                        proficiency: skill.level,
                        skillId: skill.skillId,
                    }),
                )
            }
        />
    );
};
